/**
 * @fileoverview
 * Carrega e registra traduções de blocos e extensões para locales adicionais
 * que não estão incluídos no openblock-blocks por padrão
 */

import blocksMessagesPtBr from 'openblock-l10n/editor/blocks/pt-br.json';
import extensionsMessagesPtBr from 'openblock-l10n/editor/extensions/pt-br.json';
import additionalExtensionTranslations from './extension-translations-pt-br.json';
import additionalBlockTranslations from './block-translations-pt-br.json';

/** Prefixos arduinoUno.* clonados como arduinoMega2560.* */
const MEGA2560_CLONED_PREFIXES = [
    'arduinoUno.category.pins',
    'arduinoUno.category.data',
    'arduinoUno.category.serial',
    'arduinoUno.pins.',
    'arduinoUno.data.',
    'arduinoUno.modeMenu.',
    'arduinoUno.levelMenu.',
    'arduinoUno.InterrupModeMenu.',
    'arduinoUno.dataTypeMenu.',
    'arduinoUno.eolMenu.'
];

const buildMega2560ClonedTranslations = translations => {
    const mega = {};
    for (const [key, value] of Object.entries(translations)) {
        const shouldClone = MEGA2560_CLONED_PREFIXES.some(
            prefix => key === prefix || key.startsWith(prefix)
        );
        if (!shouldClone) continue;
        const megaKey = key.replace(/^arduinoUno\./, 'arduinoMega2560.');
        if (!translations[megaKey] && !mega[megaKey]) {
            mega[megaKey] = value;
        }
    }
    return mega;
};

/**
 * Obtém todas as traduções dos blocos Arduino para passar ao VM
 * @returns {Object} Objeto com todas as traduções dos blocos Arduino
 */
export const getArduinoBlockTranslations = () => {
    // Mescla todas as traduções: blocos, extensões do pacote e traduções adicionais
    const baseTranslations = {
        ...blocksMessagesPtBr,
        ...extensionsMessagesPtBr,
        ...additionalBlockTranslations,
        ...additionalExtensionTranslations
    };
    const mega2560ClonedTranslations = buildMega2560ClonedTranslations(baseTranslations);
    const allTranslations = {
        ...baseTranslations,
        ...mega2560ClonedTranslations
    };

    // Mapeia opcodes de blocos para chaves de tradução
    const opcodeToTranslationMap = {
        // Pinos
        'arduino_pin_setPinMode': 'arduinoUno.pins.setPinMode',
        'arduino_pin_setDigitalOutput': 'arduinoUno.pins.setDigitalOutput',
        'arduino_pin_setPwmOutput': 'arduinoUno.pins.setPwmOutput',
        'arduino_pin_setServoOutput': 'arduinoUno.pins.setServoOutput',
        'arduino_pin_readDigitalPin': 'arduinoUno.pins.readDigitalPin',
        'arduino_pin_readAnalogPin': 'arduinoUno.pins.readAnalogPin',
        'arduino_pin_attachInterrupt': 'arduinoUno.pins.attachInterrupt',
        'arduino_pin_detachInterrupt': 'arduinoUno.pins.detachInterrupt',
        // Serial Uno
        'arduino_serial_serialBegin': 'arduinoUno.serial.serialBegin',
        'arduino_serial_serialPrint': 'arduinoUno.serial.serialPrint',
        'arduino_serial_serialReadAByte': 'arduinoUno.serial.serialReadAByte',
        'arduino_serial_serialAvailable': 'arduinoUno.serial.serialAvailable',
        // Serial Mega (multiSerial)
        'arduino_serial_multiSerialBegin': 'arduinoMega2560.serial.multiSerialBegin',
        'arduino_serial_multiSerialPrint': 'arduinoMega2560.serial.multiSerialPrint',
        'arduino_serial_multiSerialReadAByte': 'arduinoMega2560.serial.multiSerialReadAByte',
        'arduino_serial_multiSerialAvailable': 'arduinoMega2560.serial.multiSerialAvailable',
        // Dados (Uno e Mega compartilham opcodes)
        'arduino_data_dataConstrain': 'arduinoUno.data.dataConstrain',
        'arduino_data_dataConvert': 'arduinoUno.data.dataConvert',
        'arduino_data_dataMap': 'arduinoUno.data.dataMap',
        'arduino_data_dataConvertASCIICharacter': 'arduinoUno.data.dataConvertASCIICharacter',
        'arduino_data_dataConvertASCIINumber': 'arduinoUno.data.dataConvertASCIINumber'
    };

    // Adiciona traduções mapeadas dos opcodes
    const mappedTranslations = {};
    for (const [opcode, translationKey] of Object.entries(opcodeToTranslationMap)) {
        if (allTranslations[translationKey] && !allTranslations[opcode]) {
            mappedTranslations[opcode] = allTranslations[translationKey];
        }
    }

    // Retorna todas as traduções incluindo as mapeadas
    return {
        ...allTranslations,
        ...mappedTranslations
    };
};

/**
 * Registra traduções de blocos e extensões para pt-br no ScratchBlocks
 * @param {Object} ScratchBlocks - Instância do ScratchBlocks
 */
export const registerBlockTranslations = (ScratchBlocks) => {
    // Garante que ScratchMsgs.locales existe
    if (!ScratchBlocks.ScratchMsgs.locales) {
        ScratchBlocks.ScratchMsgs.locales = {};
    }

    // Obtém todas as traduções usando a função auxiliar
    const existingTranslations = ScratchBlocks.ScratchMsgs.locales['pt-br'] || {};
    const allTranslations = getArduinoBlockTranslations();
    
    // Mescla com traduções existentes (traduções existentes têm prioridade)
    const finalTranslations = {
        ...allTranslations,
        ...existingTranslations
    };

    // Sempre atualiza as traduções para garantir que as mais recentes sejam aplicadas
    ScratchBlocks.ScratchMsgs.locales['pt-br'] = finalTranslations;
    
    // Também adiciona as traduções para 'en' como fallback caso o locale esteja como 'en'
    // Isso garante que mesmo com locale 'en', as traduções de pt-br sejam usadas para blocos Arduino
    if (!ScratchBlocks.ScratchMsgs.locales['en']) {
        ScratchBlocks.ScratchMsgs.locales['en'] = {};
    }
    // Mescla traduções de pt-br em 'en' apenas para blocos arduino (fallback)
    // Isso permite que os blocos Arduino sejam traduzidos mesmo quando o locale é 'en'
    const enTranslations = ScratchBlocks.ScratchMsgs.locales['en'];
    const arduinoTranslations = {};
    for (const [key, value] of Object.entries(finalTranslations)) {
        if (key.startsWith('arduino') || key.startsWith('arduinoUno') || key.startsWith('arduinoMega2560') ||
            key.startsWith('arduino_pin') ||
            key.startsWith('microServo') || key.startsWith('photoResistorSensor') || 
            key.startsWith('ultrasonicSensor') || key.startsWith('linetracingIRSensor') || 
            key.startsWith('dht11Sensor')) {
            arduinoTranslations[key] = value;
        }
    }
    ScratchBlocks.ScratchMsgs.locales['en'] = {
        ...enTranslations,
        ...arduinoTranslations
    };
    
    // Garante que o currentLocale está definido se não estiver
    if (!ScratchBlocks.ScratchMsgs.currentLocale) {
        ScratchBlocks.ScratchMsgs.currentLocale = 'en';
    }
    
};

